import { api } from './api';
import type { PaymentMethod, Transaction } from './finance';

export type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface Recurrence {
  id: string;
  description: string;
  amount: string;
  direction: 'in' | 'out';
  account_id: string;
  category_id: string | null;
  payment_method: PaymentMethod | null;
  is_shared: boolean;
  frequency: Frequency;
  interval: number;
  day_of_month: number | null;
  weekday: number | null;
  start_date: string;
  end_date: string | null;
  next_run_date: string;
  active: boolean;
  is_subscription: boolean;
  vendor: string | null;
  reminder_days_before: number;
  auto_post: boolean;
  owner_user_id: string;
  created_at: string;
}

export interface RecurrenceCreateBody {
  description: string;
  amount: string;
  direction: 'in' | 'out';
  account_id: string;
  category_id?: string | null;
  payment_method?: PaymentMethod | null;
  is_shared?: boolean;
  frequency: Frequency;
  interval?: number;
  day_of_month?: number | null;
  weekday?: number | null;
  start_date: string;
  end_date?: string | null;
  is_subscription?: boolean;
  vendor?: string | null;
  reminder_days_before?: number;
  auto_post?: boolean;
}

export type RecurrenceUpdateBody = Partial<RecurrenceCreateBody> & { active?: boolean };

export const listRecurrences = () => api<Recurrence[]>('/api/v1/recurrences');

export const createRecurrence = (body: RecurrenceCreateBody) =>
  api<Recurrence>('/api/v1/recurrences', { method: 'POST', body: JSON.stringify(body) });

export const updateRecurrence = (id: string, patch: RecurrenceUpdateBody) =>
  api<Recurrence>(`/api/v1/recurrences/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteRecurrence = (id: string) =>
  api<void>(`/api/v1/recurrences/${id}`, { method: 'DELETE' });

export const runRecurrenceNow = (id: string) =>
  api<Transaction | null>(`/api/v1/recurrences/${id}/run-now`, { method: 'POST' });

export const FREQUENCY_LABEL: Record<Frequency, string> = {
  daily: 'diária',
  weekly: 'semanal',
  monthly: 'mensal',
  yearly: 'anual',
};

// convenção do backend (Python): 0 = segunda … 6 = domingo
export const WEEKDAY_LABEL = [
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
  'domingo',
];

/** Texto humano do agendamento: "todo dia 10", "toda segunda", "a cada 2 meses no dia 5"… */
export function describeSchedule(r: Recurrence): string {
  const each = r.interval > 1;
  switch (r.frequency) {
    case 'daily':
      return each ? `a cada ${r.interval} dias` : 'todos os dias';
    case 'weekly': {
      const day = r.weekday !== null ? WEEKDAY_LABEL[r.weekday] : null;
      if (each) return `a cada ${r.interval} semanas${day ? ` (${day})` : ''}`;
      return day ? `toda ${day}` : 'toda semana';
    }
    case 'monthly': {
      const day = r.day_of_month ?? Number(r.start_date.slice(8, 10));
      return each ? `a cada ${r.interval} meses no dia ${day}` : `todo dia ${day}`;
    }
    case 'yearly': {
      const [, m, d] = r.start_date.split('-');
      return each ? `a cada ${r.interval} anos em ${d}/${m}` : `todo ano em ${d}/${m}`;
    }
  }
}
